import {
    addDays,
    addMonths,
    addQuarters,
    addYears,
    endOfMonth,
    endOfQuarter,
    endOfYear,
    getDay,
    isAfter,
    isBefore,
    isWeekend,
    nextMonday,
    startOfDay,
    startOfMonth,
    startOfQuarter,
    startOfYear,
    subDays,
    subMonths,
    subWeeks,
} from "date-fns";
import { ServiceTemplateRecurrence } from "@common/schemas/serviceTemplateRecurrence.schema";
import {
    RecurrenceLeadUnit,
    RelativeCalendarPeriod,
    RelativeCalendarPosition,
    RelativeDayType,
    WorkEndDateMode,
    WorkStartDateMode,
} from "@common/types/serviceTemplateRecurrence";

interface RelativeCalendarDateOptions {
    sourceDate: Date;
    period: RelativeCalendarPeriod;
    position: RelativeCalendarPosition;
    dayType: RelativeDayType;
    dayOfPeriod: number | null;
}

interface WorkStartDateOptions {
    serviceEndDate: Date;
    recurrence: ServiceTemplateRecurrence;
}

interface WorkDateOptions {
    workStartDate: Date;
    recurrence: ServiceTemplateRecurrence;
}

function calendarPeriodStart(date: Date, period: RelativeCalendarPeriod): Date {
    if (period === RelativeCalendarPeriod.MONTH) {
        return startOfMonth(date);
    }
    if (period === RelativeCalendarPeriod.QUARTER) {
        return startOfQuarter(date);
    }
    return startOfYear(date);
}

function nextCalendarPeriodStart(
    date: Date,
    period: RelativeCalendarPeriod,
): Date {
    if (period === RelativeCalendarPeriod.MONTH) {
        return addMonths(date, 1);
    }
    if (period === RelativeCalendarPeriod.QUARTER) {
        return addQuarters(date, 1);
    }
    return addYears(date, 1);
}

function calendarPeriodEnd(date: Date, period: RelativeCalendarPeriod): Date {
    if (period === RelativeCalendarPeriod.MONTH) {
        return endOfMonth(date);
    }
    if (period === RelativeCalendarPeriod.QUARTER) {
        return endOfQuarter(date);
    }
    return endOfYear(date);
}

function relativeCalendarDate({
    sourceDate,
    period,
    position,
    dayType,
    dayOfPeriod,
}: RelativeCalendarDateOptions): Date {
    const start = calendarPeriodStart(sourceDate, period);
    const targetStart =
        position === RelativeCalendarPosition.SAME
            ? start
            : nextCalendarPeriodStart(start, period);
    const targetEnd = calendarPeriodEnd(targetStart, period);
    if (dayType === RelativeDayType.LAST) {
        return startOfDay(targetEnd);
    }
    if (dayType === RelativeDayType.FIRST) {
        return targetStart;
    }
    const candidate = addDays(targetStart, (dayOfPeriod ?? 1) - 1);
    return isAfter(candidate, targetEnd) ? targetEnd : candidate;
}

export function getWorkStartDate({
    serviceEndDate,
    recurrence,
}: WorkStartDateOptions): Date {
    if (
        recurrence.workStartDateMode ===
        WorkStartDateMode.OFFSET_DAYS_AFTER_SERVICE_END
    ) {
        const offsetDate = addDays(
            serviceEndDate,
            recurrence.workStartOffsetDays ?? 0,
        );
        // "In case of weekend next week day" — only applies to the offset
        // mode; other modes either land on a specific weekday by design
        // (NEXT_WEEKDAY_AFTER_SERVICE_END) or are positioned by the caller's
        // own relative-calendar rules.
        return recurrence.workStartSkipWeekend && isWeekend(offsetDate)
            ? nextMonday(offsetDate)
            : offsetDate;
    }
    if (
        recurrence.workStartDateMode ===
        WorkStartDateMode.NEXT_WEEKDAY_AFTER_SERVICE_END
    ) {
        const weekDay = recurrence.workStartWeekday ?? 1;
        const nextDate = addDays(serviceEndDate, 1);
        return addDays(nextDate, (weekDay - getDay(nextDate) + 7) % 7);
    }
    return relativeCalendarDate({
        sourceDate: serviceEndDate,
        period:
            recurrence.workStartRelativePeriod ?? RelativeCalendarPeriod.MONTH,
        position:
            recurrence.workStartRelativePosition ??
            RelativeCalendarPosition.NEXT,
        dayType: recurrence.workStartRelativeDayType ?? RelativeDayType.FIRST,
        dayOfPeriod: recurrence.workStartRelativeDayOfPeriod,
    });
}

export function getWorkEndDate({
    workStartDate,
    recurrence,
}: WorkDateOptions): Date {
    if (
        recurrence.workEndDateMode ===
        WorkEndDateMode.OFFSET_DAYS_AFTER_WORK_START
    ) {
        return addDays(workStartDate, recurrence.workEndOffsetDays ?? 0);
    }
    const dayType = recurrence.workEndRelativeDayType ?? RelativeDayType.LAST;
    const endDate = relativeCalendarDate({
        sourceDate: workStartDate,
        period:
            recurrence.workEndRelativePeriod ?? RelativeCalendarPeriod.MONTH,
        position:
            recurrence.workEndRelativePosition ?? RelativeCalendarPosition.SAME,
        dayType,
        dayOfPeriod: recurrence.workEndRelativeDayOfPeriod,
    });
    // A "last day" work-end (of the month/quarter/year work period) should land
    // on a weekday — roll Sat/Sun back to the preceding Friday.
    if (dayType === RelativeDayType.LAST) {
        let weekdayEnd = endDate;
        while (isWeekend(weekdayEnd)) {
            weekdayEnd = subDays(weekdayEnd, 1);
        }
        // Guard against an inverted work window: when the work start itself
        // lands on the final Sat/Sun of the month, rolling month-end back to
        // the preceding Friday would place the end before the start.
        return isBefore(weekdayEnd, workStartDate) ? workStartDate : weekdayEnd;
    }
    return endDate;
}

export function getApplicationDate({
    workStartDate,
    recurrence,
}: WorkDateOptions): Date {
    let calculated = subDays(workStartDate, recurrence.applicationLeadValue);
    if (recurrence.applicationLeadUnit === RecurrenceLeadUnit.WEEKS) {
        calculated = subWeeks(workStartDate, recurrence.applicationLeadValue);
    }
    if (recurrence.applicationLeadUnit === RecurrenceLeadUnit.MONTHS) {
        calculated = subMonths(workStartDate, recurrence.applicationLeadValue);
    }
    return calculated;
}
